/**
 * LauncherNote
 * ------------
 * Generic notification bubble that floats just above the Noki Copilot
 * launcher button. The component itself is content-agnostic — it only
 * knows how to render (icon, title, message, dismiss-x) and how to
 * animate in/out. The DECISION of when to show what message lives in
 * the parent (NokiCopilot) so future use-cases (network errors,
 * connection drops, capability hints, ...) can reuse this shell without
 * touching the component.
 *
 * Positioning: fixed, bottom 96px, right 24px — sits directly above the
 * 56×56 launcher button at bottom 24px, with a 16px breathing gap.
 * Width is capped at 300px so long messages wrap rather than push off
 * the launcher's anchor column.
 *
 * Styling: mirrors the existing `glassPanelStyle` (blur + saturate +
 * subtle border) so it reads as part of the same surface family as the
 * panel. A faint accent-tinted top edge gives it a "speaks-from-the-
 * button" feel without needing an explicit speech-bubble tail.
 *
 * Accessibility:
 *   - `role="status"` + `aria-live="polite"` so a screen-reader will
 *     announce the note when it appears without interrupting whatever
 *     the user is currently doing.
 *   - The X button has `aria-label="Dismiss notice"` and is keyboard-
 *     reachable like any native button.
 *   - Pointer-events stay `auto` so the X is clickable when the
 *     parent's portal root is otherwise pointer-events-none.
 *
 * Props:
 *   • visible    bool                    Drives the AnimatePresence
 *                                        enter/exit cycle. Parent
 *                                        controls visibility entirely;
 *                                        there is no internal state.
 *   • onDismiss  () => void              Called when the user clicks X.
 *   • title      string | ReactNode      Optional bold lead line.
 *   • message    string | ReactNode      Required body text.
 *   • icon       ReactNode (optional)    Rendered on the left, e.g. a
 *                                        mic-off glyph or info dot.
 *   • tone       "info" | "warning"      Default "info" — switches the
 *                                        accent stripe color.
 */

import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { glassPanelStyle, tokens } from "./styles";

const TONE_ACCENT = {
  info: tokens.accent.cyan,
  warning: "#FBBF24",
};

export function LauncherNote({
  visible,
  onDismiss,
  title,
  message,
  icon,
  tone = "info",
}) {
  const accent = TONE_ACCENT[tone] || TONE_ACCENT.info;

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div
          key="launcher-note"
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.96 }}
          transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
          role="status"
          aria-live="polite"
          style={{
            ...glassPanelStyle,
            position: "fixed",
            bottom: 96,
            right: 24,
            maxWidth: 300,
            padding: "12px 14px 12px 14px",
            borderRadius: 16,
            color: tokens.ink[100],
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            pointerEvents: "auto",
            zIndex: 2147483646,
            // The thin accent stripe along the top edge gives the bubble
            // a subtle "spoken-from-below" identity without an arrow tail.
            boxShadow: `inset 0 1.5px 0 0 ${accent}55, ${tokens.shadow.panel}`,
          }}
        >
          {icon ? (
            <div
              aria-hidden="true"
              style={{
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent,
                flexShrink: 0,
                marginTop: 1,
              }}
            >
              {icon}
            </div>
          ) : null}

          <div style={{ flex: 1, minWidth: 0 }}>
            {title ? (
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 12.5,
                  letterSpacing: 0.1,
                  color: tokens.ink[50],
                  marginBottom: 3,
                }}
              >
                {title}
              </div>
            ) : null}
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.45,
                color: tokens.ink[200],
                wordBreak: "break-word",
              }}
            >
              {message}
            </div>
          </div>

          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss notice"
            style={{
              width: 22,
              height: 22,
              border: "none",
              borderRadius: 6,
              background: "transparent",
              color: tokens.ink[300],
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "color 120ms ease, background 120ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = tokens.ink[50];
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = tokens.ink[300];
              e.currentTarget.style.background = "transparent";
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Convenience icon: a microphone with a slash through it. Used by the
 * "paused while recording" notice. Generic enough to keep colocated
 * here — if more icons land, split them into an `./icons/` folder.
 */
export function MicMutedIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
      <path d="M15 9.34V5a3 3 0 0 0-5.94-.6" />
      <path d="M5 5l14 14" />
      <path d="M19 12a7 7 0 0 1-3.05 5.78" />
      <path d="M9 19h6" />
      <path d="M12 19v3" />
    </svg>
  );
}

export default LauncherNote;
